import { collection, getDocs, doc, getDoc, addDoc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db } from './firebaseConfig';

const groupsData = {
  // Obtener todos los grupos
  getAllGroups: async () => {
    const querySnapshot = await getDocs(collection(db, 'groups'));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Obtener un grupo por su ID
  getGroupById: async (groupId) => {
    const groupRef = doc(db, 'groups', groupId);
    const groupSnap = await getDoc(groupRef);
    
    if (groupSnap.exists()) {
      return { id: groupSnap.id, ...groupSnap.data() };
    } else {
      throw new Error('Grupo no encontrado');
    }
  },

  // Obtener todos los mentores
  getAllMentors: async () => {
    const querySnapshot = await getDocs(collection(db, 'mentors'));
    return querySnapshot.docs.map(doc => ({ 
      value: doc.data().uid,
      text: doc.data().displayName,
      email: doc.data().email
    }));
  },

  // Obtener todos los cursos
  getAllCourses: async () => {
    const querySnapshot = await getDocs(collection(db, 'academyCourses'));
    return querySnapshot.docs.map(doc => ({
      value: doc.id,
      text: doc.data().name,
      description: doc.data().description,
      image: doc.data().image
    }));
  },

  // Obtener todos los estudiantes
  getAllStudents: async () => {
    const querySnapshot = await getDocs(collection(db, 'users'));
    return querySnapshot.docs.map(doc => ({
      value: doc.data().uid,
      text: doc.data().displayName,
      email: doc.data().email
    }));
  },

  // Crear un nuevo grupo
  createGroup: async (groupData) => {
    try {
      const docRef = await addDoc(collection(db, 'groups'), {
        ...groupData,
        activities: [],
        password: "0000",
        keyboardAccess: null,
        lastFloor: 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        updatedAtFloor: new Date().toISOString(),
        updatedAtPassword: new Date().toISOString()
      });
      
      const newGroup = await getDoc(docRef);
      return { id: newGroup.id, ...newGroup.data() };
    } catch (error) {
      console.error('Error al crear el grupo:', error);
      throw error;
    }
  },

  // Actualizar un grupo existente
  updateGroup: async (groupId, updateData) => {
    try {
      const groupRef = doc(db, 'groups', groupId);
      await updateDoc(groupRef, {
        ...updateData,
        updatedAt: new Date().toISOString()
      });
      
      const updatedGroup = await getDoc(groupRef);
      return { id: updatedGroup.id, ...updatedGroup.data() };
    } catch (error) {
      console.error('Error al actualizar el grupo:', error);
      throw error;
    }
  },

  // Eliminar un grupo
  deleteGroup: async (groupId) => {
    try {
      const groupRef = doc(db, 'groups', groupId);
      const groupSnap = await getDoc(groupRef);
      
      if (!groupSnap.exists()) {
        throw new Error('Grupo no encontrado');
      }

      const groupData = groupSnap.data();
      const activities = groupData.activities || [];

      // Extraer los IDs de proyectos únicos de las actividades
      const projectIds = [...new Set(activities
        .filter(activity => activity.projectUid)
        .map(activity => activity.projectUid)
      )];

      // Elimina cada proyecto asociado
      const deleteProjectPromises = projectIds.map(async (projectId) => {
        try {
          const projectRef = doc(db, 'projects', projectId);
          await deleteDoc(projectRef);
        } catch (error) {
          console.error(`Error al eliminar el proyecto ${projectId}:`, error);
          // Continuar con el proceso aunque falle la eliminación de un proyecto
        }
      });

      // Espera a que se completen todas las eliminaciones de proyectos
      await Promise.all(deleteProjectPromises);

      // Finalmente, eliminar el grupo
      await deleteDoc(groupRef);
      
      return true;
    } catch (error) {
      console.error('Error al eliminar el grupo:', error);
      throw error;
    }
  },

  // Asignar un estudiante a un grupo
  assignStudentToGroup: async (groupId, studentData) => {
    try {
      const groupRef = doc(db, 'groups', groupId);
      const groupSnap = await getDoc(groupRef);
      
      if (!groupSnap.exists()) {
        throw new Error('Grupo no encontrado');
      }

      const groupData = groupSnap.data();
      const students = groupData.students || [];
      
      // Verificar si el estudiante ya está en el grupo
      const studentExists = students.some(student => student.uid === studentData.uid);
      
      if (!studentExists) {
        students.push(studentData);
        await updateDoc(groupRef, { 
          students,
          updatedAt: new Date().toISOString()
        });
      }

      return { id: groupSnap.id, ...groupSnap.data() };
    } catch (error) {
      console.error('Error al asignar estudiante al grupo:', error);
      throw error;
    }
  },

  // Remover un estudiante de un grupo
  removeStudentFromGroup: async (groupId, studentUid) => {
    try {
      const groupRef = doc(db, 'groups', groupId);
      const groupSnap = await getDoc(groupRef);
      
      if (!groupSnap.exists()) {
        throw new Error('Grupo no encontrado');
      }

      const groupData = groupSnap.data();
      const students = groupData.students || [];
      
      // Filtrar el estudiante del array
      const updatedStudents = students.filter(student => student.uid !== studentUid);
      
      await updateDoc(groupRef, { 
        students: updatedStudents,
        updatedAt: new Date().toISOString()
      });

      return { id: groupSnap.id, ...groupSnap.data() };
    } catch (error) {
      console.error('Error al remover estudiante del grupo:', error);
      throw error;
    }
  },

  // Asignar un mentor a un grupo
  assignMentorToGroup: async (groupId, mentorData) => {
    try {
      const groupRef = doc(db, 'groups', groupId);
      await updateDoc(groupRef, { 
        mentor: mentorData,
        updatedAt: new Date().toISOString()
      });
      
      const updatedGroup = await getDoc(groupRef);
      return { id: updatedGroup.id, ...updatedGroup.data() };
    } catch (error) {
      console.error('Error al asignar mentor al grupo:', error);
      throw error;
    }
  },

  // Obtener grupos por mentor
  getGroupsByMentorId: async (mentorId) => {
    const groupsQuery = query(collection(db, 'groups'), where('mentor.uid', '==', mentorId));
    const querySnapshot = await getDocs(groupsQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Obtener grupos por estudiante
  getGroupsByStudentId: async (studentId) => {
    const groupsSnapshot = await getDocs(collection(db, 'groups'));
    return groupsSnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(group => 
        group.students?.some(student => student.uid === studentId)
      );
  }
};

export default groupsData; 